import React from 'react';
import { Link } from 'react-router-dom';
import './Friends.css';

// Who can see a friends list, as the settings name each choice
export const FRIENDS_VISIBILITY_NAMES = {
    public: 'Everyone',
    friends: 'Friends only',
    private: 'Only me'
};

// The button on another player's profile: add them, answer their request, withdraw yours, or remove them.
//   relation - what they are to the viewer ('none', 'outgoing', 'incoming', 'friends'), or null when logged out
//   busy - an answer is being sent; onAdd() asks or accepts; onRemove() withdraws, declines or removes
export class FriendButton extends React.Component {

    state = {
        // the Remove friend button has been pressed and is waiting to be confirmed
        confirming: false
    };

    componentDidUpdate(prevProps) {
        if (prevProps.relation !== this.props.relation && this.state.confirming) {
            this.setState({ confirming: false });
        }
    }

    render() {
        let { relation, busy, onAdd, onRemove } = this.props;
        if (relation === null) {
            return <Link className="pf-btn pf-btn-primary fr-btn" to="/login">Add friend</Link>;
        }
        if (relation === 'incoming') {
            return (
                <div className="fr-buttons">
                    <button type="button" className="pf-btn pf-btn-primary" disabled={busy} onClick={onAdd}>Accept friend request</button>
                    <button type="button" className="pf-btn pf-btn-ghost" disabled={busy} onClick={onRemove}>Decline</button>
                </div>
            );
        }
        if (relation === 'outgoing') {
            return (
                <div className="fr-buttons">
                    <span className="fr-state">Request sent</span>
                    <button type="button" className="pf-btn pf-btn-ghost" disabled={busy} onClick={onRemove}>Cancel request</button>
                </div>
            );
        }
        if (relation === 'friends') {
            return this.state.confirming ?
                <div className="fr-buttons" role="group" aria-label="Remove this friend?">
                    <button type="button" className="pf-btn pf-btn-danger" disabled={busy} onClick={onRemove}>{busy ? 'Removing...' : 'Remove friend'}</button>
                    <button type="button" className="pf-btn pf-btn-ghost" disabled={busy} onClick={() => this.setState({ confirming: false })}>Keep</button>
                </div>
                :
                <div className="fr-buttons">
                    <span className="fr-state fr-state-friends">
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                        Friends
                    </span>
                    <button type="button" className="pf-btn pf-btn-ghost" onClick={() => this.setState({ confirming: true })}>Remove</button>
                </div>;
        }
        return <button type="button" className="pf-btn pf-btn-primary fr-btn" disabled={busy} onClick={onAdd}>{busy ? 'Sending...' : 'Add friend'}</button>;
    }

}

// One player in a list, with whether they are online. The whole chip opens their profile; its buttons (children)
// sit above that.
function FriendChip({ friend, children }) {
    return (
        <li className="fr-chip">
            <Link to={`/profile/${encodeURIComponent(friend.username)}`}>
                <span className={friend.online ? 'fr-dot is-online' : 'fr-dot'} title={friend.online ? 'Online now' : undefined} aria-label={friend.online ? 'Online now' : undefined} />
                {friend.username}
            </Link>
            {children}
        </li>
    );
}

// The Friends section of a profile.
//   profile - the profile it is on ({ username, isMine })
//   list - what GET /friends/of gave for it ({ visible, visibility, friends }), or null while loading
//   requests - on your own profile, the requests waiting ({ incoming, outgoing }) or null
//   busyID - the player whose request is being answered; onAnswer(player, accept) answers or withdraws one
//   onSettings() - opens the account settings, where who can see the list is chosen
export function FriendsSection({ profile, list, requests, busyID, onAnswer, onSettings }) {
    let friends = list && list.visible ? list.friends : null;
    let incoming = requests ? requests.incoming : [];
    let outgoing = requests ? requests.outgoing : [];
    return (
        <section className="pf-section fr-section">
            <div className="pf-section-head">
                <h2>Friends{friends ? <span className="fr-count">{friends.length}</span> : null}</h2>
                {profile.isMine && list ?
                    <button type="button" className="pf-btn pf-btn-ghost" onClick={onSettings} title="Choose who can see your friends list">
                        Visible to: {FRIENDS_VISIBILITY_NAMES[list.visibility] || 'Everyone'}
                    </button>
                    : null}
            </div>

            {incoming.length > 0 ?
                <>
                    <h3>Friend requests</h3>
                    <ul className="fr-list">
                        {incoming.map((player) => (
                            <FriendChip key={player.id} friend={player}>
                                <span className="fr-chip-actions">
                                    <button type="button" className="fr-mini fr-mini-primary" disabled={busyID === player.id} onClick={() => onAnswer(player, true)}>Accept</button>
                                    <button type="button" className="fr-mini" disabled={busyID === player.id} onClick={() => onAnswer(player, false)}>Decline</button>
                                </span>
                            </FriendChip>
                        ))}
                    </ul>
                </>
                : null}
            {outgoing.length > 0 ?
                <>
                    <h3>Sent requests</h3>
                    <ul className="fr-list">
                        {outgoing.map((player) => (
                            <FriendChip key={player.id} friend={player}>
                                <span className="fr-chip-actions">
                                    <button type="button" className="fr-x" disabled={busyID === player.id} onClick={() => onAnswer(player, false)}
                                        aria-label={`Cancel your friend request to ${player.username}`} title="Cancel request">
                                        <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                                            <path fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                                        </svg>
                                    </button>
                                </span>
                            </FriendChip>
                        ))}
                    </ul>
                </>
                : null}
            {(incoming.length > 0 || outgoing.length > 0) && friends ? <h3>Friends</h3> : null}

            {!list ? <p className="pf-empty">Loading friends...</p>
                : !list.visible ?
                    <p className="pf-empty">
                        {list.visibility === 'friends' ? `Only ${profile.username}'s friends can see who they're friends with.` : `${profile.username} keeps their friends list private.`}
                    </p>
                : friends.length === 0 ?
                    <p className="pf-empty">{profile.isMine ? "You haven't added any friends yet. Visit a player's profile to add them." : `${profile.username} hasn't added any friends yet.`}</p>
                :
                <ul className="fr-list">
                    {friends.map((friend) => <FriendChip key={friend.id} friend={friend} />)}
                </ul>
            }
        </section>
    );
}
